<template>
  <div class="bg-surface w-full">
    <div v-if="loading" class="flex flex-col gap-4">
      <div class="grid grid-cols-[1fr_auto_auto] gap-x-8 pb-3 border-b border-gray-200">
        <Skeleton variant="text" class="h-4 w-28" />
        <Skeleton variant="text" class="h-4 w-24 hidden md:block" />
        <Skeleton variant="text" class="h-4 w-16 justify-self-end" />
      </div>

      <div
        v-for="i in 3"
        :key="i"
        class="flex flex-col md:grid md:grid-cols-[1fr_auto_auto] gap-x-8 py-5 border-b border-gray-200">
        <div class="flex flex-col gap-2">
          <Skeleton variant="text" class="h-5 w-48" />
          <Skeleton variant="text" class="h-4 w-40" />
          <Skeleton variant="text" class="h-4 w-32" />
          <Skeleton variant="text" class="h-4 w-28" />
        </div>
        <div class="flex justify-between items-center mt-3 md:hidden text-sm">
          <Skeleton variant="text" class="h-4 w-20" />
          <Skeleton variant="text" class="h-4 w-16" />
        </div>
        <Skeleton variant="text" class="hidden md:block h-4 w-20 pt-0.5" />
        <Skeleton variant="text" class="hidden md:block h-4 w-16 pt-0.5 justify-self-end" />
      </div>

      <div class="flex flex-col gap-3">
        <div class="grid grid-cols-[1fr_auto] md:grid-cols-[1fr_auto_auto] gap-x-8">
          <Skeleton variant="text" class="h-4 w-20" />
          <Skeleton variant="text" class="hidden md:block w-36 h-4" />
          <Skeleton variant="text" class="h-4 w-16 justify-self-end" />
        </div>
        <div class="grid grid-cols-[1fr_auto] md:grid-cols-[1fr_auto_auto] gap-x-8">
          <Skeleton variant="text" class="h-4 w-24" />
          <Skeleton variant="text" class="hidden md:block w-36 h-4" />
          <Skeleton variant="text" class="h-4 w-16 justify-self-end" />
        </div>
        <div class="grid grid-cols-[1fr_auto] md:grid-cols-[1fr_auto_auto] gap-x-8">
          <Skeleton variant="text" class="h-4 w-20" />
          <Skeleton variant="text" class="hidden md:block w-36 h-4" />
          <Skeleton variant="text" class="h-4 w-16 justify-self-end" />
        </div>
        <div class="grid grid-cols-[1fr_auto] md:grid-cols-[1fr_auto_auto] gap-x-8">
          <Skeleton variant="text" class="h-4 w-20" />
          <Skeleton variant="text" class="hidden md:block w-36 h-4" />
          <Skeleton variant="text" class="h-4 w-16 justify-self-end" />
        </div>
        <div class="grid grid-cols-[1fr_auto] md:grid-cols-[1fr_auto_auto] gap-x-8">
          <Skeleton variant="text" class="h-4 w-24" />
          <Skeleton variant="text" class="hidden md:block w-36 h-4" />
          <Skeleton variant="text" class="h-4 w-16 justify-self-end" />
        </div>
      </div>

      <div class="border-t border-gray-200 mt-4 pt-4 grid grid-cols-[1fr_auto] md:grid-cols-[1fr_auto_auto] gap-x-8">
        <Skeleton variant="text" class="h-5 w-28" />
        <Skeleton variant="text" class="hidden md:block w-36 h-5" />
        <Skeleton variant="text" class="h-5 w-20 justify-self-end" />
      </div>
    </div>

    <template v-else>
      <div
        v-if="showTicketInfo"
        :class="[
          compact
            ? 'grid grid-cols-[1fr_auto] gap-x-4 pb-3 border-b border-gray-200 text-forecolor'
            : 'grid grid-cols-[1fr_auto_auto] gap-x-8 pb-3 border-b border-gray-200 text-forecolor',
        ]">
        <span class="font-bold">{{ $t("Your tickets") }}</span>
        <span v-if="!compact" class="font-semibold text-sm w-36 hidden md:block">{{ $t("Quantity") }}</span>
        <span :class="compact ? 'font-bold text-sm text-right' : 'font-bold text-sm w-28 text-right hidden md:block'">{{
          $t("Total")
        }}</span>
      </div>
      <!-- Ticket rows -->
      <template v-if="showTicketInfo">
        <div
          v-for="(item, index) in tickets"
          :key="item.ticket_id"
          :class="[
            compact
              ? 'flex flex-col gap-x-4 py-5 border-b border-gray-200 mb-4'
              : 'flex flex-col md:grid md:grid-cols-[1fr_auto_auto] gap-x-8 py-5 border-b border-gray-200 mb-4',
            { 'border-b border-gray-100': index < tickets.length - 1 },
          ]">
          <!-- Left: ticket details -->
          <div class="flex flex-col gap-1">
            <p class="font-bold">{{ item.event?.event_name }}</p>
            <p class="font-medium text-sm">
              {{ item.ticket?.category_name }} |
              {{ formatDays(item.event?.date_start, item.event?.date_stop) }}
            </p>
            <ul class="mt-1 flex flex-col gap-1.5 font-normal">
              <li class="flex items-center gap-2 text-sm">
                <component
                  :is="getSportIcon(item.event?.sport_type)"
                  v-if="getSportIcon(item.event?.sport_type)"
                  class="w-4 h-4 shrink-0" />
                <svg
                  v-else
                  xmlns="http://www.w3.org/2000/svg"
                  class="w-4 h-4 shrink-0"
                  viewBox="0 0 24 24"
                  fill="currentColor">
                  <path
                    d="M12 2a9 9 0 0 0-9 9c0 2.6 1.1 5 2.9 6.7V20h12.2v-2.3A8.97 8.97 0 0 0 21 11a9 9 0 0 0-9-9zm4 13H8v-1.5c0-1.1.9-2 2-2h4c1.1 0 2 .9 2 2V15z" />
                </svg>
                {{ item.event?.tournament_name }}
              </li>
              <li class="flex items-center gap-2 text-sm">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="w-4 h-4 shrink-0"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2">
                  <rect x="3" y="4" width="18" height="18" rx="2" />
                  <line x1="16" y1="2" x2="16" y2="6" />
                  <line x1="8" y1="2" x2="8" y2="6" />
                  <line x1="3" y1="10" x2="21" y2="10" />
                </svg>
                {{ formatDates(item.event?.date_start, item.event?.date_stop) }}
              </li>
              <li class="flex items-center gap-2 text-sm">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="w-4 h-4 shrink-0"
                  viewBox="0 0 24 24"
                  fill="currentColor">
                  <path
                    d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 6.5 12 6.5s2.5 1.12 2.5 2.5S13.38 11.5 12 11.5z" />
                </svg>
                {{ item.event?.city }}
              </li>
            </ul>
          </div>

          <!-- Mobile: quantity and total on one line -->
          <div
            :class="
              compact
                ? 'flex justify-between items-center mt-3 text-sm'
                : 'flex justify-between items-center mt-3 md:hidden text-sm'
            ">
            <span class="flex gap-1">
              <span class="font-bold">{{ item.quantity }}</span> &times;
              <Euros class="text-sm font-normal" :amount="item.net_rate" />
            </span>
            <span class="font-bold"><Euros :amount="item.quantity * item.net_rate" /></span>
          </div>

          <!-- Desktop: quantity -->
          <div v-if="!compact" class="hidden md:flex w-36 items-start justify-center pt-0.5">
            <span class="text-sm flex gap-1">
              <span class="font-bold">{{ item.quantity }}</span> &times;
              <Euros class="text-sm font-normal" :amount="item.net_rate" />
            </span>
          </div>

          <!-- Desktop: line total -->
          <div v-if="!compact" class="hidden md:flex w-36 items-start justify-end pt-0.5">
            <span class="font-bold"><Euros :amount="item.quantity * item.net_rate" /></span>
          </div>
        </div>
      </template>

      <div v-if="!onlyShowTotals" class="flex flex-col font-normal">
        <!-- Subtotal -->
        <div
          v-if="!hideSubtotalAndTicketFee"
          :class="
            compact
              ? 'grid mb-3 grid-cols-[1fr_auto] gap-x-4'
              : 'grid mb-3  grid-cols-[1fr_auto] md:grid-cols-[1fr_auto_auto] gap-x-8'
          ">
          <span class="text-sm">{{ $t("Subtotal") }}</span>
          <span v-if="!compact" class="hidden md:block w-36" />
          <div :class="compact ? 'text-right flex justify-end' : 'w-28 text-right flex justify-end'">
            <Euros data-testid="subtotal" :amount="subtotal" class="font-semibold text-sm" />
          </div>
        </div>

        <!-- Bookingcosts -->
        <div
          v-if="payDetails?.bookingcosts?.sub_total > 0"
          :class="
            compact
              ? 'grid grid-cols-[1fr_auto] gap-x-4'
              : 'grid grid-cols-[1fr_auto] md:grid-cols-[1fr_auto_auto] gap-x-8'
          ">
          <span class="text-sm">{{ $t("Bookingcosts") }}</span>
          <div v-if="!compact" class="hidden md:flex w-36 text-sm gap-1">
            <strong>{{ payDetails.bookingcosts.quantity }}</strong> &times;
            <Euros data-testid="subtotal" :amount="payDetails.bookingcosts.net_rate" />
          </div>
          <div :class="compact ? 'text-right flex justify-end' : 'w-28 text-right flex justify-end'">
            <Euros :amount="payDetails.bookingcosts.sub_total" class="font-semibold text-sm" />
          </div>
        </div>

        <!-- Ticket fee -->
        <div
          v-if="payDetails?.ticket_fee?.sub_total > 0 && !hideSubtotalAndTicketFee"
          :class="
            compact
              ? 'grid grid-cols-[1fr_auto] gap-x-4'
              : 'grid grid-cols-[1fr_auto] md:grid-cols-[1fr_auto_auto] gap-x-8'
          ">
          <span class="text-sm">{{ $t("Ticket fee") }}</span>
          <div v-if="!compact" class="hidden md:flex w-36 text-sm gap-1">
            <span class="font-bold">{{ payDetails.ticket_fee.quantity }}</span
            >&times;
            <Euros class="text-sm font-normal" :amount="payDetails.ticket_fee.net_rate" />
          </div>
          <div :class="compact ? 'text-right flex justify-end' : 'w-28 text-right flex justify-end'">
            <Euros data-testid="ticketFee" :amount="payDetails.ticket_fee.sub_total" class="font-semibold text-sm" />
          </div>
        </div>

        <!-- Payment fee -->
        <div
          v-if="hasPaymentFee"
          :class="
            compact
              ? 'grid grid-cols-[1fr_auto] gap-x-4'
              : 'grid grid-cols-[1fr_auto] md:grid-cols-[1fr_auto_auto] gap-x-8'
          ">
          <span class="text-sm">{{ $t("Payment fee") }}</span>
          <span v-if="!compact" class="hidden md:block w-36" />
          <div :class="compact ? 'text-right flex justify-end' : 'w-28 text-right flex justify-end'">
            <Euros :amount="payDetails.payment_fee.sub_total" class="font-semibold text-sm" />
          </div>
        </div>

        <!-- VAT Total -->
        <div
          v-if="totalVat"
          :class="
            compact
              ? 'grid grid-cols-[1fr_auto] gap-x-4 items-center'
              : 'grid grid-cols-[1fr_auto] md:grid-cols-[1fr_auto_auto] gap-x-8 items-center'
          ">
          <div v-if="!hideSubtotalAndTicketFee">
            <button
              class="flex items-center gap-1 text-sm cursor-pointer hover: transition-colors w-fit min-h-[44px]"
              @click="showVatDetails = !showVatDetails">
              {{ $t("VAT total") }}{{ reverseChargeApplied ? "*" : "" }}
              <ChevronDownIcon :class="`w-4 h-4 transition-transform ${showVatDetails ? 'rotate-180' : ''}`" />
            </button>
          </div>
          <div v-else class="text-sm">
            <div v-if="payDetails?.payment_fee?.vat">
              {{
                $t("VAT {percentage}% on {subject}", {
                  percentage: payDetails.payment_fee.vat_rate,
                  subject: $t("payment fee").toLowerCase(),
                })
              }}
            </div>
          </div>
          <span v-if="!compact" class="hidden md:block w-36" />
          <div :class="compact ? 'text-right flex justify-end' : 'w-28 text-right flex justify-end'">
            <Euros data-testid="VAT" :amount="totalVat" class="font-semibold text-sm" />
          </div>
        </div>

        <!-- VAT breakdown (expanded) -->
        <Transition
          enter-active-class="transition-all duration-200 ease-out"
          enter-from-class="opacity-0 -translate-y-1"
          enter-to-class="opacity-100 translate-y-0"
          leave-active-class="transition-all duration-150 ease-in"
          leave-from-class="opacity-100 translate-y-0"
          leave-to-class="opacity-0 -translate-y-1">
          <div
            v-if="showVatDetails && !hideSubtotalAndTicketFee"
            class="bg-surface rounded-lg px-4 py-3 flex flex-col gap-2">
            <template v-for="[, value] in Array.from(vatGroupsTickets?.entries() || [])" :key="value.rate">
              <div v-if="value.amount" class="grid grid-cols-[1fr_auto] gap-x-8 text-sm">
                <div class="flex gap-1 items-center">
                  <ArrowTurnDownRightIcon class="size-3" />
                  <span>
                    {{
                      $t("VAT {percentage}% on {subject}", {
                        percentage: value.rate,
                        subject: $t("tickets").toLowerCase(),
                      })
                    }}
                    ({{ Array.from(eventCountries).join(", ") }})
                  </span>
                </div>
                <div class="text-right flex justify-end"><Euros :amount="value.amount" class="text-sm" /></div>
              </div>
            </template>
            <div v-if="payDetails?.ticket_fee?.vat" class="grid grid-cols-[1fr_auto] gap-x-8 text-sm">
              <div class="flex gap-1 items-center">
                <ArrowTurnDownRightIcon class="size-3" />
                <span>
                  {{
                    $t("VAT {percentage}% on {subject}", {
                      percentage: payDetails.ticket_fee.vat_rate,
                      subject: $t("ticket fee").toLowerCase(),
                    })
                  }}
                </span>
              </div>
              <div class="text-right flex justify-end">
                <Euros :amount="payDetails.ticket_fee.vat" class="text-sm" />
              </div>
            </div>
            <div v-if="payDetails?.bookingcosts?.vat" class="grid grid-cols-[1fr_auto] gap-x-8 text-sm">
              <div class="flex gap-1 items-center">
                <ArrowTurnDownRightIcon class="size-3" />
                <span>
                  {{
                    $t("VAT {percentage}% on {subject}", {
                      percentage: payDetails.bookingcosts.vat_rate,
                      subject: $t("bookingcosts").toLowerCase(),
                    })
                  }}
                </span>
              </div>
              <div class="text-right flex justify-end">
                <Euros :amount="payDetails.bookingcosts.vat" class="text-sm" />
              </div>
            </div>
            <div v-if="payDetails?.payment_fee?.vat" class="grid grid-cols-[1fr_auto] gap-x-8 text-sm">
              <div class="flex gap-1 items-center">
                <ArrowTurnDownRightIcon class="size-3" />
                <span>
                  {{
                    $t("VAT {percentage}% on {subject}", {
                      percentage: payDetails.payment_fee.vat_rate,
                      subject: $t("payment fee").toLowerCase(),
                    })
                  }}
                </span>
              </div>
              <div class="text-right flex justify-end"><Euros :amount="payDetails.payment_fee.vat" /></div>
            </div>
          </div>
        </Transition>
      </div>

      <!-- Order total -->
      <div
        class="border-t border-gray-200 mt-4 pt-4"
        :class="
          compact
            ? 'grid grid-cols-[1fr_auto] gap-x-4'
            : 'grid grid-cols-[1fr_auto] md:grid-cols-[1fr_auto_auto] gap-x-8'
        ">
        <span class="font-bold text-lg">{{ $t("Order total") }}</span>
        <span v-if="!compact" class="hidden md:block w-36" />
        <div :class="compact ? 'text-right flex justify-end' : 'w-28 text-right flex justify-end'">
          <Euros data-testid="totalAmount" :amount="payDetails?.total.sub_total" class="font-bold text-lg" />
        </div>
      </div>
    </template>
  </div>
</template>
<script setup lang="ts">
import Euros from "@/components/Euros.vue";
import { computed, ref } from "vue";
import { useCartStore } from "@/store/cart.ts";
import { ChevronDownIcon, ArrowTurnDownRightIcon } from "@heroicons/vue/24/outline";
import { formatDays, formatDates } from "@/helpers/format";
import { sportTypes } from "@xs2event/js-lib/util";
import { Skeleton } from "@xs2event/js-lib/components";

/* eslint-disable @typescript-eslint/no-explicit-any */
const props = withDefaults(
  defineProps<{
    payDetails?: any;
    hideSubtotalAndTicketFee?: boolean;
    onlyShowTotals?: boolean;
    tickets?: any;
    showTicketInfo?: boolean;
    compact?: boolean;
    loading?: boolean;
  }>(),
  {
    payDetails: null,
    hideSubtotalAndTicketFee: false,
    onlyShowTotals: false,
    tickets: null,
    showTicketInfo: false,
    compact: false,
    loading: false,
  }
);

const cartStore = useCartStore();

const payDetails = computed(() => props.payDetails ?? cartStore.payDetails);

const subtotal = computed(() => {
  const details = payDetails.value;
  let total = 0;
  if (details?.tickets) {
    details.tickets.forEach((ticket) => {
      total += ticket.sub_total_exc_vat;
    });
  }
  return total;
});

const showVatDetails = ref(false);

const eventCountries = computed(() => [...new Set((props.tickets || []).map((ticket) => ticket.event?.iso_country))]);

const vatGroupsTickets = computed(() => {
  const grouped = payDetails.value?.tickets.reduce((map, { vat_rate, vat_amount, ticket_id }) => {
    const country = cartStore.items.find((x) => x.ticket.ticket_id == ticket_id)?.event.iso_country;

    if (!vat_amount) return map;

    if (!map.has(vat_rate)) {
      map.set(vat_rate, { amount: 0, rate: vat_rate, countries: new Set() });
    }

    const entry = map.get(vat_rate);
    entry.amount += vat_amount;
    entry.countries.add(country);
    entry.rate = vat_rate;

    return map;
  }, new Map());

  if (!grouped) return;

  return grouped as Map<number, { rate: number; amount: number; countries: Set<string> }>;
});

const totalTicketsVat = computed(() => {
  return (
    (vatGroupsTickets.value &&
      Array.from(vatGroupsTickets.value.values()).reduce((previous, current) => previous + current.amount, 0)) ||
    0
  );
});

const totalVat = computed(() => {
  const ticketFee = payDetails?.value?.ticket_fee?.vat || 0;
  const paymentFee = payDetails?.value?.payment_fee?.vat || 0;
  const bookingcosts = payDetails?.value?.bookingcosts?.vat || 0;

  const ticketVatTotal = totalTicketsVat.value;

  return ticketFee + ticketVatTotal + bookingcosts + paymentFee;
});

const hasPaymentFee = computed(() => {
  if (payDetails.value?.payment_fee) {
    if (payDetails.value?.payment_fee.sub_total > 0) {
      return Object.keys(payDetails.value.payment_fee).length > 0;
    }
  }

  return false;
});

const reverseChargeApplied = computed(() => {
  return payDetails.value?.tickets?.some((x) => x.reverse_charge_applied) ?? false;
});

const getSportIcon = (sportType?: string) => {
  return sportType ? sportTypes[sportType]?.icon : undefined;
};
</script>
